import React, { useState, useRef, useCallback } from 'react';
import { Upload, Loader2, ImagePlus, AlertTriangle } from 'lucide-react';
import { base44 } from '@/api/base44Client';

/**
 * Drag-and-drop / click-to-select image upload zone.
 * Props:
 *  - onUploaded(file_url, file): called for each successfully uploaded image
 *  - multiple?: boolean (default true)
 *  - label?: string
 *  - compact?: boolean — smaller height for inline use
 *  - existing?: string[] — already-added image URLs, used for filename-based duplicate checks
 *
 * Duplicate detection: hashes each dropped file (SHA-256 of its bytes) and
 * compares against files already uploaded this session, plus a filename
 * match against the `existing` URLs. Duplicates are skipped with a warning.
 */
export async function sha256(file) {
  try {
    const buf = await file.arrayBuffer();
    const digest = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return '';
  }
}
function urlFilename(u) {
  try {
    return decodeURIComponent(String(u).split('/').pop().split('?')[0] || '').toLowerCase();
  } catch {
    return '';
  }
}

export default function ImageDropZone({ onUploaded, multiple = true, label, compact = false, existing = [] }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(0);
  const [dupMsg, setDupMsg] = useState('');
  const inputRef = useRef(null);
  const fingerprints = useRef(new Set());

  const uploadFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith('image/'));
    if (!files.length) return;
    // `existing` may be an array of URL strings or { url, hash } items.
    const existingItems = (existing || []).map((e) => (typeof e === 'string' ? { url: e } : e));
    const existingUrls = existingItems.map((e) => e.url).filter(Boolean);
    const existingHashes = new Set(existingItems.map((e) => e.hash).filter(Boolean));
    const hashed = await Promise.all(files.map(async (f) => ({ file: f, fp: await sha256(f) })));

    const seen = new Set(fingerprints.current);
    const toUpload = [];
    let dups = 0;
    for (const h of hashed) {
      const name = h.file.name.toLowerCase();
      const isDup =
        (h.fp && seen.has(h.fp)) ||
        (h.fp && existingHashes.has(h.fp)) ||
        existingUrls.some((u) => urlFilename(u) === name);
      if (isDup) { dups++; continue; }
      if (h.fp) seen.add(h.fp);
      toUpload.push(h);
    }
    fingerprints.current = seen;

    if (dups > 0) {
      setDupMsg(`${dups} photo${dups > 1 ? 's' : ''} already added — skipped`);
      setTimeout(() => setDupMsg(''), 4000);
    }
    if (!toUpload.length) return;

    setUploading((n) => n + toUpload.length);
    await Promise.all(toUpload.map(async ({ file, fp }) => {
      try {
        const { file_url } = await base44.integrations.Core.UploadFile({ file });
        onUploaded?.(file_url, file, fp);
      } finally {
        setUploading((n) => n - 1);
      }
    }));
  }, [onUploaded, existing]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    uploadFiles(e.dataTransfer.files);
  }, [uploadFiles]);

  const onDragOver = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setDragging(true); }, []);
  const onDragLeave = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setDragging(false); }, []);

  return (
    <div>
      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onClick={() => inputRef.current?.click()}
        className={`relative cursor-pointer border-2 border-dashed transition-all flex flex-col items-center justify-center text-center gap-1.5 ${compact ? 'p-3' : 'p-6'} ${dragging ? 'border-primary bg-primary/10' : 'border-white/15 hover:border-primary/40 hover:bg-white/[0.02]'}`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple={multiple}
          className="hidden"
          onChange={(e) => { uploadFiles(e.target.files); e.target.value = ''; }}
        />
        {uploading > 0 ? (
          <>
            <Loader2 className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} text-primary animate-spin`} />
            <span className="text-primary text-[10px] font-bold tracking-widest">UPLOADING {uploading}…</span>
          </>
        ) : (
          <>
            {dragging ? <ImagePlus className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} text-primary`} /> : <Upload className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} text-white/40`} />}
            <span className={`${compact ? 'text-[10px]' : 'text-xs'} font-bold tracking-wider text-white/50`}>
              {label || (compact ? 'DROP PHOTOS' : 'DRAG PHOTOS HERE OR CLICK TO BROWSE')}
            </span>
          </>
        )}
      </div>
      {dupMsg && (
        <div className="mt-2 flex items-center gap-2 bg-amber-500/5 border border-amber-500/30 px-3 py-2">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0" />
          <span className="text-amber-300 text-[11px] font-medium">{dupMsg}</span>
        </div>
      )}
    </div>
  );
}